import React from 'react'
import styled from '@emotion/styled'
import { bool, object, shape, string } from 'prop-types'
import dayjs from 'dayjs'

// Import Components
import { HeroContext } from './HeroContext'
import { HeroToolBar } from '../HeroToolBar'
import { makeImgResizerUrl } from '@jelly/utils'
import { SponsoredLink } from '../SponsoredLink'
import { AuthorByline } from '../AuthorByline'

// Styled Components via Emotion
// HeroBanner
const Author = styled.div`
	display: inline-block;
`
const AuthorType = styled.div`
	display: inline-block;
`
const DateWrapper = styled.div`
	display: inline-block;
`

const HeroBannerContainer = styled.div`
	box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.2);
	height: 100%;
	position: relative;
	width: 100%;
`
const HeroContextWrapper = styled.div`
	${({ theme }) => theme.maxWidth.L};
	${({ sponsorType }) => !sponsorType && 'margin: 30px auto 0 auto;'}
	display: inline-block;
	align-items: center;
	${({ theme }) => theme.MQ.M} {
		position: static;
	}
`
const Header = styled.h1`
	font-size: 1.3rem;
	font-weight: 300;
	margin: 10px 0;
	text-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);

	${({ theme }) => theme.MQ.S} {
		font-size: 30px;
	}

	${({ theme }) => theme.MQ.M} {
		font-size: 34px;
		margin: 10px 0 20px 0;
	}
`
const HeaderSponsorLogo = styled.img`
	display: block;
	height: auto;
	margin: 5px auto;
	max-height: 40px;
	max-width: 175px;
	width: auto;

	${({ theme }) => theme.MQ.M} {
		display: inline;
		margin: 0 8px;
		max-height: 50px;
		max-width: 200px;
		vertical-align: middle;
	}
`
const ImageCreditWrapper = styled.div`
	bottom: 3px;
	position: absolute;
	visibility: hidden;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		bottom: 60px;
	}
	${({ theme }) => theme.MQ.L} {
		visibility: visible;
	}
`
const ImageCredit = styled.div`
	color: rgba(255, 255, 255, 0.8);
	font-size: 9px;
	text-align: center;
	text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);

	${({ theme }) => theme.MQ.M} {
		font-size: 12px;
		margin-right: 30px;
		text-align: right;
	}
`
const SubHeader = styled.div`
	display: block;
	font-size: 1rem;
	font-weight: 300;
	text-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);
	${({ theme }) => theme.MQ.S} {
		font-size: 20px;
	}
	${({ theme }) => theme.MQ.M} {
		align-items: center;
		font-size: 25px;
		justify-content: center;
	}
`
const SponsoredByContainer = styled.div`
	align-items: center;
	display: flex;
	justify-content: center;
	margin-top: 10px;
`

export const ContentHeroBannerSubHeader = ({
	author: { name, type = 'Contributing Writer' },
	date,
	published,
	sponsorship = { author: null, sponsorType: null, thumbnail: null, name: null },
}) => {
	const byAuthorLink = (
		<>
			{'By '}
			<Author> {name} </Author>
			{', '}
			<AuthorType>
				<AuthorByline type={type} />
			</AuthorType>
			{Date.parse(published) < Date.parse(date) ? ' | Updated ' : ' | '}
			<DateWrapper>{dayjs(date).format('MMM DD, YYYY')}</DateWrapper>
		</>
	)
	const getSubHeaderContentByType = ({ sponsorType, thumbnail, name: sponsorName }) => {
		switch (sponsorType) {
			//advertorial and single-sponsor uses same subheader although image is taken from different places
			// single-sponsor logos should not be clickable at this time
			case 'single-sponsor':
				return (
					<>
						Sponsored Content from{' '}
						<HeaderSponsorLogo
							alt={sponsorName || name}
							className="lazyload"
							data-src={makeImgResizerUrl({
								src: thumbnail,
								method: 'h',
								h: '50-png',
							})}
							advertorial
						/>{' '}
						<DateWrapper>{dayjs(date).format('MMM DD, YYYY')}</DateWrapper>
					</>
				)
			case 'advertorial':
				return (
					<>
						Sponsored Content from{' '}
						<SponsoredLink sponsorName={sponsorName}>
							<HeaderSponsorLogo
								alt={sponsorName || name}
								className="lazyload"
								data-src={makeImgResizerUrl({
									src: thumbnail,
									method: 'h',
									h: '50-png',
								})}
								advertorial
							/>{' '}
						</SponsoredLink>
						<DateWrapper>{dayjs(date).format('MMM DD, YYYY')}</DateWrapper>
					</>
				)
			case 'hub-sponsor':
				return (
					<>
						{byAuthorLink}
						<br />
						<SponsoredByContainer>
							Sponsored by{' '}
							<SponsoredLink sponsorName={sponsorName}>
								<HeaderSponsorLogo
									alt={sponsorName}
									className="lazyload"
									data-src={makeImgResizerUrl({ src: thumbnail, method: 'h', h: '50-png' })}
								/>
							</SponsoredLink>
						</SponsoredByContainer>
					</>
				)
			default:
				return byAuthorLink
		}
	}
	return (sponsorship && getSubHeaderContentByType(sponsorship)) || byAuthorLink
}

ContentHeroBannerSubHeader.propTypes = {
	author: shape({
		name: string,
		slug: string,
		type: string,
	}),
	date: string.isRequired,
	published: string.isRequired,
	sponsorship: object,
}

/**
 * This is the Content Hero Banner for content pages (ie: article, category and review pages)
 * Title is center aligned, and contains a tool bar at the bottom
 * With social Media buttons and breadcrumbs
 */
export function ContentHeroBanner({
	author,
	breadcrumbs,
	date,
	header,
	imageSrc: { caption, isBlurred, path },
	published,
	sponsorship = { sponsorType: null },
}) {
	return (
		<HeroBannerContainer>
			<HeroContext
				align="center"
				background={path}
				isBlurred={isBlurred}
				height="375px"
				overlayColor="#000000"
				overlayOpacity=".45"
			>
				<HeroContextWrapper sponsorType={sponsorship && sponsorship.sponsorType}>
					<Header> {header} </Header>
					<SubHeader>
						<ContentHeroBannerSubHeader
							author={author}
							date={date}
							published={published}
							sponsorship={sponsorship}
						/>
					</SubHeader>
				</HeroContextWrapper>
			</HeroContext>
			{caption && (
				<ImageCreditWrapper>
					<ImageCredit>Image Credit: {caption}</ImageCredit>
				</ImageCreditWrapper>
			)}
			<HeroToolBar breadcrumbs={breadcrumbs} />
		</HeroBannerContainer>
	)
}

ContentHeroBanner.propTypes = {
	author: shape({
		name: string,
		slug: string,
		type: string,
	}),
	breadcrumbs: object.isRequired,
	date: string.isRequired,
	header: string.isRequired,
	imageSrc: shape({ caption: string, isBlurred: bool, path: string }),
	published: string.isRequired,
	sponsorship: object,
}
